import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert, Button } from '@mui/joy';
import { CheckCircle as SuccessIcon, XCircle as ErrorIcon } from '@phosphor-icons/react';
import { api } from '../../lib/api';

export const OAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');
      
      // Get platform from URL path
      const pathParts = window.location.pathname.split('/');
      const platform = pathParts[pathParts.indexOf('oauth') + 1];

      if (error) {
        setStatus('error');
        setMessage(errorDescription || 'Authorization was denied');
        return;
      }

      if (!code) {
        setStatus('error');
        setMessage('No authorization code received');
        return;
      }

      try {
        // Exchange code for access token
        const response = await api.post('/api/marketing/social-accounts/oauth-callback/', {
          platform,
          code,
          state,
          redirect_uri: `${window.location.origin}/marketing/oauth/${platform}/callback`
        });

        const responseData = response.data as any;
        if (responseData.success) {
          setStatus('success');
          setMessage(responseData.message || 'Account connected successfully!');
          
          // Close popup and refresh parent
          setTimeout(() => {
            if (window.opener) {
              window.close();
            } else {
              navigate('/marketing/social-media');
            }
          }, 2000);
        } else {
          setStatus('error');
          setMessage(responseData.error || 'Failed to connect account');
        }
      } catch (err: any) {
        setStatus('error');
        setMessage(err.response?.data?.error || 'An error occurred while connecting your account');
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        p: 3
      }}
    >
      {status === 'processing' && (
        <>
          <CircularProgress size="lg" sx={{ mb: 3 }} />
          <Typography level="h4" sx={{ mb: 1 }}>
            Connecting your account...
          </Typography>
          <Typography level="body-md">
            Please wait while we complete the authorization process.
          </Typography>
        </>
      )}

      {status === 'success' && (
        <>
          <SuccessIcon size={64} color="var(--joy-palette-success-500)" style={{ marginBottom: 24 }} />
          <Typography level="h4" sx={{ mb: 1 }}>
            Success!
          </Typography>
          <Typography level="body-md" sx={{ mb: 3 }}>
            {message}
          </Typography>
          <Typography level="body-sm">
            This window will close automatically...
          </Typography>
        </>
      )}

      {status === 'error' && (
        <>
          <ErrorIcon size={64} color="var(--joy-palette-danger-500)" style={{ marginBottom: 24 }} />
          <Typography level="h4" sx={{ mb: 1 }}>
            Connection Failed
          </Typography>
          <Alert color="danger" sx={{ mb: 3 }}>
            {message}
          </Alert>
          <Button
            onClick={() => {
              if (window.opener) {
                window.close();
              } else {
                navigate('/marketing/social-media');
              }
            }}
          >
            Close
          </Button>
        </>
      )}
    </Box>
  );
};